import { useState } from "react"
import { useTranslation } from "react-i18next"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { DropdownSelect } from "@/components/ui/dropdown-select"
import { BetterDocInsuranceCountrySelect } from "@/modules/admin/better-doc/components/better-doc-insurance-country-select"
import { BetterDocOpenRecommendationTable } from "@/modules/admin/better-doc/components/better-doc-open-recommendation-table"
import { BetterDocRemoteRequestStatus } from "@/modules/admin/better-doc/components/better-doc-remote-request-status"
import {
  BETTER_DOC_OPEN_LIST_KINDS,
  BetterDocOpenListKind,
  DEFAULT_INSURANCE_COUNTRY,
} from "@/modules/admin/better-doc/constants/better-doc-constants"
import { useBetterDocFetch } from "@/modules/admin/better-doc/hooks/useBetterDocFetch"
import { findEnumValue } from "@/modules/admin/better-doc/lib/find-enum-value"
import {
  BetterDocOpenRecommendationsResult,
  fetchBetterDocRecommendedRecommendations,
} from "@/modules/admin/better-doc/services/betterDocRecommendedRecommendationsQuery"
import { fetchBetterDocSelectedRecommendations } from "@/modules/admin/better-doc/services/betterDocSelectedRecommendationsQuery"
import { Country } from "@/types/__generated__/union-types"

type OpenRecommendationsRequest = {
  insuranceCountry: Country
  listKind: BetterDocOpenListKind
  attempt: number
}

const fetchBetterDocOpenRecommendations = ({
  insuranceCountry,
  listKind,
}: OpenRecommendationsRequest): Promise<BetterDocOpenRecommendationsResult> =>
  listKind === "recommended"
    ? fetchBetterDocRecommendedRecommendations(insuranceCountry)
    : fetchBetterDocSelectedRecommendations(insuranceCountry)

export const BetterDocOpenRecommendations = () => {
  const { t } = useTranslation()
  const [openRequest, setOpenRequest] = useState<OpenRecommendationsRequest>({
    insuranceCountry: DEFAULT_INSURANCE_COUNTRY,
    listKind: "recommended",
    attempt: 0,
  })
  const openState = useBetterDocFetch(openRequest, fetchBetterDocOpenRecommendations)

  const listOptions = BETTER_DOC_OPEN_LIST_KINDS.map((listKind) => ({
    value: listKind,
    label: t(`admin.betterDoc.openRecommendations.list.${listKind}`),
  }))

  const handleInsuranceCountryChange = (insuranceCountry: Country) => {
    setOpenRequest((currentRequest) => ({ ...currentRequest, insuranceCountry, attempt: currentRequest.attempt + 1 }))
  }

  const handleListKindSelect = (selectedValue: string | null | undefined) => {
    const listKind = findEnumValue(BETTER_DOC_OPEN_LIST_KINDS, selectedValue)
    if (listKind !== null) {
      setOpenRequest((currentRequest) => ({ ...currentRequest, listKind, attempt: currentRequest.attempt + 1 }))
    }
  }

  const handleLoad = () => {
    setOpenRequest((currentRequest) => ({ ...currentRequest, attempt: currentRequest.attempt + 1 }))
  }

  const renderOpenState = () => {
    switch (openState.status) {
      case "idle":
      case "loading":
        return <LoadingSpinner size={24} className="flex justify-center p-8" />
      case "failed":
        return (
          <Banner type="inline" variant="critical" data-testid="better-doc-admin-open-recommendations-error">
            {t("admin.betterDoc.requestFailed")}
          </Banner>
        )
      case "loaded":
        return (
          <div className="flex flex-col gap-4">
            <BetterDocRemoteRequestStatus
              status={openState.data.status}
              httpStatusCode={openState.data.httpStatusCode}
              errorReason={openState.data.errorReason}
              exchangeId={openState.data.exchangeId}
            />
            {openState.data.status === "RECEIVED" ? (
              <BetterDocOpenRecommendationTable
                recommendations={openState.data.recommendations}
                listKind={openState.request.listKind}
              />
            ) : null}
          </div>
        )
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="better-doc-admin-open-recommendations">
      <div className="flex flex-wrap items-start gap-4">
        <BetterDocInsuranceCountrySelect
          insuranceCountry={openRequest.insuranceCountry}
          onInsuranceCountryChange={handleInsuranceCountryChange}
          testId="better-doc-admin-open-recommendations-insurance-country"
        />
        <DropdownSelect
          label={t("admin.betterDoc.openRecommendations.listLabel")}
          options={listOptions}
          defaultValue={openRequest.listKind}
          onSelect={handleListKindSelect}
          className="w-56"
          testId="better-doc-admin-open-recommendations-list"
        />
        <div className="flex h-12 items-center">
          <Button
            type="button"
            variant="secondary"
            disabled={openState.status === "loading"}
            onClick={handleLoad}
            data-testid="better-doc-admin-open-recommendations-load-button">
            {t("admin.betterDoc.openRecommendations.load")}
          </Button>
        </div>
      </div>
      <p className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-open-recommendations-hint">
        {t("admin.betterDoc.openRecommendations.sharedStagingHint")}
      </p>
      {renderOpenState()}
    </div>
  )
}
